import axios from "axios";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
});

/**
 * One page of the Facility's Patient roster.
 *
 * Its own endpoint rather than paging get_patient_list_with_aabha_info: that one
 * has ~23 callers and lives in views.py, which this project no longer accepts
 * changes to. Leaving it untouched means those callers keep running byte-identical
 * code. Rows carry no photo — the Patient Overview fetches the one it displays.
 *
 * @returns {Promise<{patients: object[], totalCount: number}>}
 */
export const fetchPatientRoster = ({ page, pageSize, search, signal }) =>
  axios
    .get(`${REACT_APP_BACKEND_ROUTE_URL}get_patient_roster/`, {
      headers: authHeaders(),
      params: {
        page,
        page_size: pageSize,
        ...(search ? { search } : {}),
      },
      signal,
    })
    .then((res) => {
      // This backend answers 200 with status "Failed" for real problems, e.g.
      // "User does not have access to endpoint". Treating that as an empty list
      // would show "no patients registered" for what is actually a failure.
      if (res.data?.status !== "Success") {
        throw new Error(res.data?.message || "Patient list request failed");
      }
      const rows = res.data.data || [];
      return {
        patients: rows,
        // Older deployments predate total_count; fall back to what we received
        // so paging still behaves rather than collapsing to "0 patients".
        totalCount:
          typeof res.data.total_count === "number"
            ? res.data.total_count
            : rows.length,
      };
    });

/**
 * Full demographics for one Patient — the fields a roster row does not carry
 * (full address, insurance schemes, height/weight, photo).
 *
 * @returns {Promise<object>}
 */
export const fetchPatientDemographics = ({ patientId, signal }) =>
  axios
    .post(
      `${REACT_APP_BACKEND_ROUTE_URL}get_filtered_patient_data/`,
      {
        filter_type: "patient_id",
        filter_details: { patient_id: patientId },
      },
      { headers: authHeaders(), signal },
    )
    .then((res) => {
      // Throws rather than returning null, so every read in this module fails the
      // same way and the backend's own message survives to the caller.
      if (res.data?.status !== "Success") {
        throw new Error(res.data?.message || "Patient details request failed");
      }
      return res.data.patient_details;
    });

/**
 * A Patient's Care Contexts, newest first — shown to users as "Visit Purposes".
 *
 * The doctor's name is deliberately not sourced here: the only endpoint carrying
 * it for a care context is restricted to doctor users and is broken for every
 * other role. Doctor names come from Past Appointments instead.
 *
 * Note the misspelled `care_contenxts` response key; the typo is in the API.
 *
 * Returns each raw Care Context (`care_context_reference`, `description`,
 * `display`, `timestamp`, `hip_id`, `unique_identifier`, `abdm_linked`) with
 * `reference`, `visitDetail` and `visitDateTime` added as aliases.
 *
 * @returns {Promise<object[]>}
 */
export const fetchPatientCareContexts = ({ patientId, signal }) =>
  axios
    .post(
      `${REACT_APP_BACKEND_ROUTE_URL}fetch_care_contexts_by_filter/`,
      {
        filter_type: "patient_id",
        filter_details: { patient_id: patientId },
      },
      { headers: authHeaders(), signal },
    )
    .then((res) => {
      // A Patient with no Care Context still answers "Success" with an empty
      // list, so "Failed" here is a genuine problem and must surface as one
      // rather than be indistinguishable from "never been seen".
      if (res.data?.status !== "Success") {
        throw new Error(res.data?.message || "Visit history request failed");
      }
      return (res.data.care_contenxts || []).map((ctx) => ({
        // Every raw field is preserved deliberately. The shared PastVisits
        // component renders `${description} ${care_context_reference}` in its
        // selector, so remapping to a subset silently breaks that dropdown.
        // Convenience aliases are added alongside, never instead.
        ...ctx,
        reference: ctx.care_context_reference,
        visitDetail: ctx.display || "",
        visitDateTime: ctx.timestamp || "",
      }));
    });

/**
 * A Patient's Past Appointments, newest first — doctor, date and outcome.
 *
 * Local Data: this is the Patient's history at THIS Facility, and it is the one
 * thing neither screen this module replaces ever showed.
 *
 * @returns {Promise<Array<{appointmentId: string, doctorName: string, startTime: string, status: string, mode: string}>>}
 */
export const fetchPatientAppointments = ({ patientId, signal }) =>
  axios
    .get(`${REACT_APP_BACKEND_ROUTE_URL}get_patient_appointments/`, {
      headers: authHeaders(),
      params: { patient_id: patientId },
      signal,
    })
    .then((res) => {
      if (res.data?.status !== "Success") {
        throw new Error(
          res.data?.message || "Appointment history request failed",
        );
      }
      return (res.data.data || []).map((row) => ({
        appointmentId: row.appointment_id,
        doctorName: row.doctor_name || "",
        startTime: row.appointment_start_time || "",
        status: row.status || "",
        mode: row.appointment_mode || "",
      }));
    });

/** True when an error is just an aborted in-flight request, not a failure. */
export const isAbortError = (err) =>
  axios.isCancel(err) ||
  err?.name === "CanceledError" ||
  err?.code === "ERR_CANCELED";
